import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, MenuItem, Select, FormControl,
  InputLabel, RadioGroup, FormControlLabel, Radio, Checkbox, Alert, CircularProgress,
  Paper, Chip, Divider, Autocomplete,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import GroupIcon from '@mui/icons-material/Group';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonIcon from '@mui/icons-material/Person';
import HotelIcon from '@mui/icons-material/Hotel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';
const FORM_VERSION = 'v3-descuento-comision';  // bust browser cache

const FORMA_PAGO_OPCIONES = [
  { value: 'credito', label: 'Crédito agencia', desc: 'La agencia recibió el dinero (total o parcial). Se carga al cupo.' },
  { value: 'datafono', label: 'Datáfono Entretur', desc: 'Cliente paga con la máquina de la empresa. Voucher por WhatsApp.' },
  { value: 'transferencia', label: 'Transferencia', desc: 'La agencia transfiere a Entretur. Adjunta el comprobante. Queda en HOLD hasta verificación.' },
  { value: 'bold_link', label: 'Link de pago BOLD', desc: 'El turista paga directo a Entretur con tarjeta o PSE. No carga al cupo. Reserva queda HOLD hasta confirmar pago.' },
  { value: 'muelle', label: 'Pago en muelle', desc: 'El turista paga al embarcar. Cupo afectado hasta cobro.' },
];

function fmtMoney(n) {
  return '$' + new Intl.NumberFormat('es-CO', { minimumFractionDigits: 0 }).format(Number(n || 0));
}

export default function ReservaForm() {
  const { token } = useParams();
  const [contexto, setContexto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // Campos del form
  const [agenciaId, setAgenciaId] = useState('');  // editable: NARA puede no haberla identificado o haberla detectado mal
  const [paqueteId, setPaqueteId] = useState('');
  const [salidaId, setSalidaId] = useState('');
  const [adultos, setAdultos] = useState(1);
  const [ninos, setNinos] = useState(0);
  const [bebes, setBebes] = useState(0);
  const [cortesias, setCortesias] = useState(0);
  // Migración 084: cortesías diferenciadas. Mantenemos `cortesias` como suma derivada.
  const [cortesiasAdultos, setCortesiasAdultos] = useState(0);
  const [cortesiasNinos, setCortesiasNinos] = useState(0);
  const [cortesiasMotivo, setCortesiasMotivo] = useState('');
  const [cortesiasAutorizadaPorNombre, setCortesiasAutorizadaPorNombre] = useState('');
  const [formaPago, setFormaPago] = useState('credito');
  const [montoCobrado, setMontoCobrado] = useState(0);
  const [comisionAnticipadaCheck, setComisionAnticipadaCheck] = useState(false);
  const [comisionAnticipada, setComisionAnticipada] = useState(0);
  const [descuentoComisionCheck, setDescuentoComisionCheck] = useState(false);
  const [descuentoComision, setDescuentoComision] = useState(0);
  // Soporte para forma_pago='transferencia'
  const [referenciaTransferencia, setReferenciaTransferencia] = useState('');
  const [comprobanteUrl, setComprobanteUrl] = useState('');
  const [comprobanteNombre, setComprobanteNombre] = useState('');
  const [subiendoComprobante, setSubiendoComprobante] = useState(false);
  const [montoTransferido, setMontoTransferido] = useState('');     // vacío = total
  const [saldoACargoDe, setSaldoACargoDe] = useState('muelle');     // default muelle
  const [titularNombre, setTitularNombre] = useState('');
  const [cedulaTitular, setCedulaTitular] = useState('');
  const [whatsappCliente, setWhatsappCliente] = useState('');
  const [hotelHuesped, setHotelHuesped] = useState('');
  const [tiqueteAgencia, setTiqueteAgencia] = useState('');
  const [observaciones, setObservaciones] = useState('');
  // Consentimiento DIMAR: el cliente acepta que se le tome foto + documento al embarcar
  const [consentimientoDimar, setConsentimientoDimar] = useState(false);

  // Cargar contexto al montar
  useEffect(() => {
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await fetch(`${API_BASE}/turismo/reserva-form/contexto`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Error ${res.status}`);
        }
        const data = await res.json();
        setContexto(data);

        // Agencia: pre-seleccionar la que viene del token (si NARA la identificó).
        // Si no, el intermediario la elige del dropdown manualmente.
        if (data.agencia && data.agencia.id) setAgenciaId(data.agencia.id);

        // Pre-llenar desde contexto_inicial del JWT
        const ctx = data.contexto_inicial || {};
        if (ctx.salida_id) setSalidaId(ctx.salida_id);
        // paqueteId: prioridad al JWT, fallback a derivar de la salida
        if (ctx.tour_paquete_id) {
          setPaqueteId(ctx.tour_paquete_id);
        } else if (ctx.salida_id && Array.isArray(data.salidas)) {
          const s = data.salidas.find(x => x.id === ctx.salida_id);
          if (s) setPaqueteId(s.paquete_id);
        }
        // Pre-llenado completo (caso intermediario: NARA extrajo del bloque texto)
        if (ctx.adultos !== undefined && ctx.adultos !== null) setAdultos(ctx.adultos);
        else if (ctx.num_personas) setAdultos(ctx.num_personas);
        if (ctx.ninos !== undefined && ctx.ninos !== null) setNinos(ctx.ninos);
        if (ctx.bebes !== undefined && ctx.bebes !== null) setBebes(ctx.bebes);
        if (ctx.cortesias !== undefined && ctx.cortesias !== null) setCortesias(ctx.cortesias);
        if (ctx.titular_nombre) setTitularNombre(ctx.titular_nombre);
        if (ctx.cedula_titular) setCedulaTitular(ctx.cedula_titular);
        if (ctx.whatsapp_cliente) setWhatsappCliente(ctx.whatsapp_cliente);
        if (ctx.hotel_huesped) setHotelHuesped(ctx.hotel_huesped);
        if (ctx.tiquete_agencia) setTiqueteAgencia(ctx.tiquete_agencia);
        if (ctx.observaciones) setObservaciones(ctx.observaciones);
        if (ctx.forma_pago && ['datafono', 'credito', 'muelle', 'transferencia', 'bold_link'].includes(ctx.forma_pago)) {
          setFormaPago(ctx.forma_pago);
        }
        if (ctx.monto_cobrado !== undefined && ctx.monto_cobrado !== null) {
          setMontoCobrado(ctx.monto_cobrado);
        }
        if (ctx.comision_anticipada && Number(ctx.comision_anticipada) > 0) {
          setComisionAnticipadaCheck(true);
          setComisionAnticipada(Number(ctx.comision_anticipada));
        }

        // Restaurar autoguardado si existe
        const autoSaved = localStorage.getItem(`reservaform_${token.slice(-12)}`);
        if (autoSaved) {
          try {
            const a = JSON.parse(autoSaved);
            if (a.titularNombre) setTitularNombre(a.titularNombre);
            if (a.cedulaTitular) setCedulaTitular(a.cedulaTitular);
            if (a.whatsappCliente) setWhatsappCliente(a.whatsappCliente);
            if (a.hotelHuesped) setHotelHuesped(a.hotelHuesped);
            if (a.tiqueteAgencia) setTiqueteAgencia(a.tiqueteAgencia);
            if (a.observaciones) setObservaciones(a.observaciones);
            if (a.formaPago) setFormaPago(a.formaPago);
            if (a.montoCobrado) setMontoCobrado(a.montoCobrado);
            if (a.adultos) setAdultos(a.adultos);
            if (a.ninos) setNinos(a.ninos);
            if (a.bebes) setBebes(a.bebes);
            if (a.paqueteId) setPaqueteId(a.paqueteId);
            if (a.salidaId) setSalidaId(a.salidaId);
            if (a.agenciaId) setAgenciaId(a.agenciaId);
            if (typeof a.comisionAnticipadaCheck === 'boolean') setComisionAnticipadaCheck(a.comisionAnticipadaCheck);
            if (a.comisionAnticipada) setComisionAnticipada(a.comisionAnticipada);
            if (typeof a.descuentoComisionCheck === 'boolean') setDescuentoComisionCheck(a.descuentoComisionCheck);
            if (a.descuentoComision) setDescuentoComision(a.descuentoComision);
          } catch {}
        }
      } catch (e) {
        setError(e.message);
      }
      setLoading(false);
    })();
  }, [token]);

  // Autoguardado
  useEffect(() => {
    if (!contexto) return;
    localStorage.setItem(`reservaform_${token.slice(-12)}`, JSON.stringify({
      agenciaId, paqueteId, salidaId, adultos, ninos, bebes, formaPago, montoCobrado,
      comisionAnticipadaCheck, comisionAnticipada, descuentoComisionCheck, descuentoComision,
      titularNombre, cedulaTitular, whatsappCliente, hotelHuesped, tiqueteAgencia, observaciones,
    }));
  }, [contexto, agenciaId, paqueteId, salidaId, adultos, ninos, bebes, formaPago, montoCobrado,
      comisionAnticipadaCheck, comisionAnticipada, descuentoComisionCheck, descuentoComision,
      titularNombre, cedulaTitular, whatsappCliente, hotelHuesped, tiqueteAgencia, observaciones, token]);

  // Filtrar salidas por paquete seleccionado
  const salidasDelPaquete = useMemo(() => {
    if (!contexto || !paqueteId) return [];
    return contexto.salidas.filter(s => s.paquete_id === paqueteId);
  }, [contexto, paqueteId]);

  const salidaSeleccionada = useMemo(() => {
    if (!salidaId || !contexto) return null;
    return contexto.salidas.find(s => s.id === salidaId);
  }, [salidaId, contexto]);

  // Calcular precio total. Las cortesías NO pagan, así que se restan del pax pagante.
  const precioTotal = useMemo(() => {
    if (!salidaSeleccionada) return 0;
    const pagantesAdultos = Math.max(0, adultos - cortesiasAdultos);
    const pagantesNinos = Math.max(0, ninos - cortesiasNinos);
    return (
      pagantesAdultos * (salidaSeleccionada.precio_adulto || 0)
      + pagantesNinos * (salidaSeleccionada.precio_nino || 0)
      + bebes * (salidaSeleccionada.precio_bebe || 0)
    );
  }, [salidaSeleccionada, adultos, ninos, bebes, cortesiasAdultos, cortesiasNinos]);

  // Resolver la agencia activa: la del dropdown si está seleccionada (con sus
  // overrides de comisión cargados via /contexto cuando viene del token), o un
  // fallback al item simplificado de agencias_disponibles cuando el intermediario
  // eligió otra distinta a la del token.
  const agenciaActiva = useMemo(() => {
    if (!contexto) return null;
    if (contexto.agencia && contexto.agencia.id === agenciaId) return contexto.agencia;
    return (contexto.agencias_disponibles || []).find(a => a.id === agenciaId) || null;
  }, [contexto, agenciaId]);

  // Comisión total esperada de la reserva
  // = (override por paquete si existe) o (default de la agencia)
  // Aplica sobre num_pax = adultos + niños (bebés no cuentan).
  // Las cortesías NO generan comisión → se calcula sobre pagantes.
  const comisionTotal = useMemo(() => {
    if (!agenciaActiva || !paqueteId || !precioTotal) return 0;
    const pagantesAdultos = Math.max(0, (adultos || 0) - (cortesiasAdultos || 0));
    const pagantesNinos = Math.max(0, (ninos || 0) - (cortesiasNinos || 0));
    const numPax = pagantesAdultos + pagantesNinos;
    if (numPax <= 0) return 0;
    const ag = agenciaActiva;
    const ovr = (ag.comision_por_paquete || {})[paqueteId];
    const tipo = (ovr && ovr.tipo) || ag.tipo_comision || 'porcentaje';
    if (tipo === 'valor_fijo') {
      // PRIORIDAD: diferenciados (mig. 074). Si están presentes, sale de ahí.
      const vAdulto = ovr && ovr.valor_adulto != null ? Number(ovr.valor_adulto) : null;
      const vNino   = ovr && ovr.valor_nino   != null ? Number(ovr.valor_nino)   : null;
      if (vAdulto != null || vNino != null) {
        return (vAdulto || 0) * pagantesAdultos + (vNino || 0) * pagantesNinos;
      }
      // Fallback legacy: valor fijo plano por pax.
      const valor = (ovr && ovr.valor_fijo) || ag.valor_comision_fijo || 0;
      return valor * numPax;
    } else {
      // Porcentaje — diferenciados primero, luego plano.
      const pAdulto = ovr && ovr.porcentaje_adulto != null ? Number(ovr.porcentaje_adulto) : null;
      const pNino   = ovr && ovr.porcentaje_nino   != null ? Number(ovr.porcentaje_nino)   : null;
      const precioAdulto = salidaSeleccionada?.precio_adulto || 0;
      const precioNino   = salidaSeleccionada?.precio_nino   || 0;
      if (pAdulto != null || pNino != null) {
        return Math.round(
          (precioAdulto * pagantesAdultos * (pAdulto || 0)
           + precioNino * pagantesNinos * (pNino || 0)) / 100
        );
      }
      const pct = (ovr && ovr.porcentaje) || ag.porcentaje_comision || 0;
      return Math.round(precioTotal * pct / 100);
    }
  }, [agenciaActiva, paqueteId, precioTotal, adultos, ninos, cortesiasAdultos, cortesiasNinos, salidaSeleccionada]);

  // Auto-clamp del anticipo y descuento cuando comisionTotal baja por cambio de
  // agencia/tour/pax. Sin esto, el usuario ve un anticipo "stale" y la validación
  // del submit falla con "anticipada (X) supera comisión total (Y)" al cambiar agencia.
  useEffect(() => {
    if (comisionAnticipadaCheck && Number(comisionAnticipada) > comisionTotal) {
      setComisionAnticipada(comisionTotal);
    }
  }, [comisionTotal]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const antEf = comisionAnticipadaCheck ? Number(comisionAnticipada) || 0 : 0;
    const descMax = Math.max(comisionTotal - antEf, 0);
    if (descuentoComisionCheck && Number(descuentoComision) > descMax) {
      setDescuentoComision(descMax);
    }
  }, [comisionTotal, comisionAnticipada, comisionAnticipadaCheck]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setError(null);
    if (!agenciaId) return setError('Selecciona la agencia de la reserva.');
    if (!paqueteId || !salidaId) return setError('Tour o fecha no disponibles. Pídele a NARA un nuevo link.');
    if ((adultos + ninos) <= 0) return setError('Debe haber al menos un adulto o niño.');
    // Validación cortesías
    const _totalCort = (cortesiasAdultos || 0) + (cortesiasNinos || 0);
    if (cortesiasAdultos > adultos) return setError(`No puede haber más cortesías de adulto (${cortesiasAdultos}) que adultos (${adultos}).`);
    if (cortesiasNinos > ninos) return setError(`No puede haber más cortesías de niño (${cortesiasNinos}) que niños (${ninos}).`);
    if (_totalCort > 0) {
      if (!cortesiasAutorizadaPorNombre.trim()) return setError('Indica quién autorizó la cortesía (nombre).');
      if (!cortesiasMotivo.trim()) return setError('Indica el motivo de la cortesía.');
      // Cortesías y descuento sobre comisión PUEDEN coexistir: una reserva puede tener
      // ciertas personas como cortesía (no pagan) y descuento sobre la comisión que
      // aplica a los pagantes restantes.
    }
    if (!titularNombre.trim()) return setError('Falta nombre del titular.');
    if (!cedulaTitular.trim()) return setError('Falta cédula del titular.');
    if (!whatsappCliente.trim()) return setError('Falta WhatsApp del cliente.');
    if (!formaPago) return setError('Selecciona forma de pago.');
    if (!consentimientoDimar) return setError('Debes aceptar el consentimiento DIMAR (foto del titular al embarcar).');

    const comAntFinal = comisionAnticipadaCheck ? Number(comisionAnticipada) || 0 : 0;
    const descComFinal = descuentoComisionCheck ? Number(descuentoComision) || 0 : 0;
    if (comAntFinal > 0 && comAntFinal > comisionTotal + 0.01) {
      return setError(`La comisión anticipada (${fmtMoney(comAntFinal)}) supera la comisión total de la reserva (${fmtMoney(comisionTotal)}).`);
    }
    if (descComFinal > 0 && descComFinal > comisionTotal + 0.01) {
      return setError(`El descuento (${fmtMoney(descComFinal)}) supera la comisión total (${fmtMoney(comisionTotal)}). La agencia no puede regalar más de lo que gana.`);
    }
    if ((comAntFinal + descComFinal) > comisionTotal + 0.01) {
      return setError(`Comisión anticipada (${fmtMoney(comAntFinal)}) + descuento (${fmtMoney(descComFinal)}) superan la comisión total (${fmtMoney(comisionTotal)}).`);
    }

    // Validación específica para transferencia
    if (formaPago === 'transferencia') {
      if (!referenciaTransferencia.trim()) {
        return setError('Indica la referencia de la transferencia.');
      }
      if (!comprobanteUrl) {
        return setError('Adjunta el comprobante de la transferencia.');
      }
      const mt = Number(montoTransferido) || precioTotal;
      if (mt < 0 || mt > precioTotal + 0.01) {
        return setError(`El monto transferido debe estar entre 0 y ${fmtMoney(precioTotal)}.`);
      }
      const diff = precioTotal - mt;
      if (diff > 0.01) {
        if (!['agencia', 'muelle'].includes(saldoACargoDe)) {
          return setError('Indica a cargo de quién queda el saldo de la transferencia parcial.');
        }
      }
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/turismo/reserva-form/aplicar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agencia_id: agenciaId,
          paquete_id: paqueteId,
          salida_id: salidaId,
          adultos, ninos, bebes,
          cortesias: Math.max(cortesias, (cortesiasAdultos || 0) + (cortesiasNinos || 0)),
          cortesias_adultos: cortesiasAdultos || 0,
          cortesias_ninos: cortesiasNinos || 0,
          cortesias_motivo: ((cortesiasAdultos || 0) + (cortesiasNinos || 0)) > 0 ? cortesiasMotivo.trim() : null,
          cortesias_autorizada_por_nombre: ((cortesiasAdultos || 0) + (cortesiasNinos || 0)) > 0 ? cortesiasAutorizadaPorNombre.trim() : null,
          forma_pago: formaPago,
          monto_cobrado: formaPago === 'credito' ? Number(montoCobrado) : 0,
          comision_anticipada: comAntFinal,
          descuento_comision: descComFinal,
          titular_nombre: titularNombre.trim(),
          cedula_titular: cedulaTitular.trim(),
          whatsapp_cliente: whatsappCliente.trim(),
          hotel_huesped: hotelHuesped.trim() || null,
          tiquete_agencia: tiqueteAgencia.trim() || null,
          observaciones: observaciones.trim() || null,
          referencia_transferencia: formaPago === 'transferencia' ? referenciaTransferencia.trim() : null,
          comprobante_url: formaPago === 'transferencia' ? comprobanteUrl : null,
          monto_transferido: formaPago === 'transferencia' ? (Number(montoTransferido) || precioTotal) : null,
          saldo_a_cargo_de: (formaPago === 'transferencia' && (Number(montoTransferido) || precioTotal) < precioTotal) ? saldoACargoDe : null,
          consentimiento_dimar: consentimientoDimar,
        }),
      });
      // Parse robusto: si el backend devuelve HTML (error 500 sin handler de JSON),
      // mostrar un mensaje amigable en lugar de "Unexpected token <".
      let data;
      const respText = await res.text();
      try {
        data = JSON.parse(respText);
      } catch (parseErr) {
        const previa = respText.slice(0, 100).replace(/\s+/g, ' ');
        throw new Error(
          `Error inesperado al procesar la reserva (HTTP ${res.status}). ` +
          `Por favor reintenta en unos segundos. Si el problema persiste, contacta a soporte. ` +
          `[Detalle técnico: ${previa}…]`
        );
      }
      if (!res.ok) {
        // Backend devolvió JSON con detail — usamos ese mensaje directamente.
        throw new Error(data.detail || `Error ${res.status}: no se pudo procesar la reserva.`);
      }
      localStorage.removeItem(`reservaform_${token.slice(-12)}`);
      setSuccess(data);
    } catch (e) {
      // Si fue error de red (fetch rechazado), mostrar mensaje claro
      const msg = e?.message || 'Error desconocido';
      const friendly = msg.includes('Failed to fetch') || msg.includes('NetworkError')
        ? 'Sin conexión al servidor. Verifica tu internet e intenta de nuevo.'
        : msg;
      setError(friendly);
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !contexto) {
    return (
      <Box sx={{ p: 3, maxWidth: 480, mx: 'auto', mt: 6 }}>
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
          <ErrorOutlineIcon sx={{ fontSize: 60, color: '#dc2626', mb: 1 }} />
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#0f172a', mb: 1 }}>
            Link no válido
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#475569' }}>
            {error.includes('TOKEN_EXPIRADO')
              ? 'El link expiró. Pídele uno nuevo a NARA por WhatsApp.'
              : 'No se pudo abrir el formulario. Pídele un nuevo link a NARA por WhatsApp.'}
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (success) {
    const compartirBoldWa = () => {
      if (!success.payment_url) return;
      const tel = (whatsappCliente || '').replace(/\D/g, '');
      const msg = encodeURIComponent(
        `Hola ${titularNombre}, este es el link de pago para tu reserva ${success.codigo_reserva}:\n${success.payment_url}\n\nMonto: ${fmtMoney(success.precio_total)}\n\nUna vez completes el pago, tu reserva quedará confirmada.`
      );
      const wa = tel ? `https://wa.me/${tel}?text=${msg}` : `https://wa.me/?text=${msg}`;
      window.open(wa, '_blank');
    };

    // Si la reserva es con Bold, renderiza el componente que hace polling y
    // cambia la UI según el estado del pago.
    if (success.payment_url && success.payment_link_id) {
      return (
        <PantallaPagoBold
          success={success}
          titularNombre={titularNombre}
          whatsappCliente={whatsappCliente}
          compartirBoldWa={compartirBoldWa}
          onReintentar={() => setSuccess(null)}  // vuelve al formulario para generar nuevo link
        />
      );
    }

    return (
      <Box sx={{ p: 3, maxWidth: 480, mx: 'auto', mt: 6 }}>
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
          <CheckCircleIcon sx={{ fontSize: 72, color: '#10b981', mb: 1.5 }} />
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
            ¡Reserva creada!
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#64748b', mb: 2 }}>
            Código:
          </Typography>
          <Chip
            label={success.codigo_reserva}
            sx={{
              fontSize: 18, fontWeight: 700, py: 2.5, px: 1, mb: 2,
              bgcolor: '#0d9488', color: '#fff',
            }}
          />

          {/* Link Bold generado: bloque con URL + 3 acciones */}
          {success.payment_url && (
            <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: '#f5f3ff', border: '1px solid #c4b5fd' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#5b21b6', mb: 1 }}>
                💳 Link de pago BOLD generado
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#475569', mb: 1.5, textAlign: 'left' }}>
                La reserva queda en HOLD. Usa <strong>Pagar ahora</strong> si tú haces el pago al instante,
                o <strong>Enviar por WhatsApp</strong> para que el turista lo pague desde su celular.
                El pago se confirma automáticamente cuando Bold notifique.
              </Typography>
              <Box sx={{ p: 1, bgcolor: '#fff', borderRadius: 1, mb: 1, border: '1px solid #ddd6fe' }}>
                <Typography sx={{ fontSize: 11, fontFamily: 'monospace', wordBreak: 'break-all', color: '#0f172a' }}>
                  {success.payment_url}
                </Typography>
              </Box>
              {/* Acción primaria: pagar ahora */}
              <Button fullWidth size="medium" variant="contained"
                onClick={() => window.open(success.payment_url, '_blank', 'noopener,noreferrer')}
                sx={{ textTransform: 'none', fontWeight: 700, mb: 1,
                       background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' }}>
                💳 Pagar ahora
              </Button>
              {/* Acciones secundarias */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button fullWidth size="small" variant="outlined"
                  onClick={() => navigator.clipboard?.writeText(success.payment_url)}
                  sx={{ textTransform: 'none', borderColor: '#7c3aed', color: '#7c3aed' }}>
                  Copiar URL
                </Button>
                <Button fullWidth size="small" variant="contained" onClick={compartirBoldWa}
                  sx={{ textTransform: 'none', fontWeight: 700,
                         background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}>
                  Enviar por WhatsApp
                </Button>
              </Box>
            </Box>
          )}

          <Typography sx={{ fontSize: 13, color: '#475569', whiteSpace: 'pre-line', textAlign: 'left', mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
            {success.mensaje}
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b', mt: 2 }}>
            Recibirás esta confirmación por WhatsApp también.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 480, mx: 'auto', pb: 8 }}>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3, background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)', color: '#fff' }}>
        <Typography sx={{ fontSize: 11, opacity: 0.85, textTransform: 'uppercase', letterSpacing: 1 }}>
          Nueva reserva
        </Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
          {agenciaActiva?.nombre || 'Selecciona agencia ↓'}
        </Typography>
        {contexto.intermediario ? (
          <Box sx={{ mt: 0.5 }}>
            <Typography sx={{ fontSize: 12, opacity: 0.9 }}>
              📨 Procesada por: <strong>{contexto.intermediario.nombre}</strong> (Entretur)
            </Typography>
            {contexto.intermediario.vendedor_externo_nombre && (
              <Typography sx={{ fontSize: 12, opacity: 0.9 }}>
                A solicitud de: <strong>{contexto.intermediario.vendedor_externo_nombre}</strong>
              </Typography>
            )}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 12, opacity: 0.9 }}>
            Vendedor: {contexto.vendedor.nombre || contexto.vendedor.whatsapp}
          </Typography>
        )}
      </Paper>

      {/* Agencia — siempre editable; NARA puede no haberla identificado o haberla detectado mal */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', mb: 1.5 }}>
          🏢 Agencia que hace la reserva
        </Typography>
        <Autocomplete
          size="small"
          fullWidth
          options={contexto.agencias_disponibles || []}
          value={(contexto.agencias_disponibles || []).find(a => a.id === agenciaId) || null}
          onChange={(_, agencia) => setAgenciaId(agencia ? agencia.id : '')}
          getOptionLabel={(o) => o?.nombre || ''}
          isOptionEqualToValue={(a, b) => a?.id === b?.id}
          autoHighlight
          openOnFocus
          noOptionsText="Sin coincidencias — prueba con otra parte del nombre"
          filterOptions={(options, { inputValue }) => {
            // Búsqueda tolerante: sin acentos, ignora mayúsculas, match en cualquier
            // parte del nombre. Ordenamos: 1º coincidencias al inicio, 2º el resto.
            const norm = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
            const q = norm(inputValue.trim());
            if (!q) return options;
            const startsWith = [], contains = [];
            for (const a of options) {
              const n = norm(a.nombre);
              if (n.startsWith(q)) startsWith.push(a);
              else if (n.includes(q)) contains.push(a);
            }
            return [...startsWith, ...contains];
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="🔍 Buscar agencia (escribe para filtrar)"
              placeholder="Ej. Talaso · Experiencia · Cartagena Paradise…"
              helperText={`${(contexto.agencias_disponibles || []).length} agencias disponibles`}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{option.nombre}</Typography>
              {(option.ciudad || option.tipo) && (
                <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>
                  {[option.tipo, option.ciudad].filter(Boolean).join(' · ')}
                </Typography>
              )}
            </Box>
          )}
        />
        {!agenciaId && (
          <Typography sx={{ fontSize: 11, color: '#dc2626', mt: 0.75 }}>
            ⚠️ NARA no identificó la agencia desde el mensaje. Selecciónala arriba.
          </Typography>
        )}
        {agenciaId && contexto.agencia && contexto.agencia.id !== agenciaId && (
          <Typography sx={{ fontSize: 11, color: '#b45309', mt: 0.75 }}>
            ⚠️ Cambiaste la agencia que NARA detectó. La reserva se asociará a la elegida arriba.
          </Typography>
        )}
      </Paper>

      {/* Tour y fecha — pre-llenados desde la consulta a NARA, no editables */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', mb: 1.5 }}>
          <EventIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
          Tour y fecha
        </Typography>
        {(() => {
          // Fuente principal: el contexto_inicial del JWT (siempre presente cuando NARA armó el link).
          // Fallback: salidaSeleccionada (si vino de la lista de /contexto).
          const ctxIni = (contexto && contexto.contexto_inicial) || {};
          const tourNombre = (salidaSeleccionada && salidaSeleccionada.paquete_nombre)
            || ctxIni.tour_nombre
            || (contexto.paquetes || []).find(p => p.id === ctxIni.tour_paquete_id)?.nombre;
          const fechaTour = (salidaSeleccionada && salidaSeleccionada.fecha) || ctxIni.fecha;
          const horaSalida = salidaSeleccionada && salidaSeleccionada.hora_salida;
          const cuposDisp = salidaSeleccionada && salidaSeleccionada.cupos_disponibles;
          if (tourNombre && fechaTour) {
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, bgcolor: '#f0fdfa', borderRadius: 2, border: '1px solid #99f6e4' }}>
                  <Typography sx={{ fontSize: 11, color: '#0f766e', fontWeight: 600, textTransform: 'uppercase', minWidth: 50 }}>Tour</Typography>
                  <Typography sx={{ fontSize: 14, color: '#0f172a', fontWeight: 600 }}>
                    {tourNombre}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, bgcolor: '#f0fdfa', borderRadius: 2, border: '1px solid #99f6e4' }}>
                  <Typography sx={{ fontSize: 11, color: '#0f766e', fontWeight: 600, textTransform: 'uppercase', minWidth: 50 }}>Fecha</Typography>
                  <Typography sx={{ fontSize: 14, color: '#0f172a', fontWeight: 600 }}>
                    {fechaTour} {horaSalida && `· ${horaSalida}`}
                  </Typography>
                  {cuposDisp !== undefined && (
                    <Chip size="small" label={`${cuposDisp} cupos`} sx={{ ml: 'auto', bgcolor: '#fff', color: '#0d9488', fontSize: 10, fontWeight: 700 }} />
                  )}
                </Box>
                <Typography sx={{ fontSize: 10, color: '#64748b', mt: 0.5, textAlign: 'center' }}>
                  Si necesitas otro tour o fecha, pídele a NARA un nuevo link por WhatsApp.
                </Typography>
              </Box>
            );
          }
          return (
            <Alert severity="warning" sx={{ fontSize: 12 }}>
              Este link no tiene tour ni fecha definidos. Pídele a NARA un nuevo link por WhatsApp con la fecha específica de la reserva.
            </Alert>
          );
        })()}
      </Paper>

      {/* PAX */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', mb: 1 }}>
          <GroupIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
          Personas
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
          <TextField label="Adultos" type="number" size="small" value={adultos}
            onChange={e => setAdultos(Math.max(0, parseInt(e.target.value) || 0))}
            inputProps={{ min: 0 }} />
          <TextField label="Niños" type="number" size="small" value={ninos}
            onChange={e => setNinos(Math.max(0, parseInt(e.target.value) || 0))}
            inputProps={{ min: 0 }} />
          <TextField label="Bebés" type="number" size="small" value={bebes}
            onChange={e => setBebes(Math.max(0, parseInt(e.target.value) || 0))}
            inputProps={{ min: 0 }} />
        </Box>

        {/* Cortesías diferenciadas (Mig. 084) */}
        <Box sx={{ mt: 1.5, p: 1.25, border: '1px dashed #fbbf24', borderRadius: 2, bgcolor: '#fffbeb' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#92400e', mb: 0.75 }}>
            🎁 Cortesías (opcional · no pagan, no generan comisión)
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
            <TextField label="Cortesías adultos" type="number" size="small"
              value={cortesiasAdultos}
              onChange={e => setCortesiasAdultos(Math.max(0, Math.min(adultos, parseInt(e.target.value) || 0)))}
              inputProps={{ min: 0, max: adultos }}
              helperText={`Máx ${adultos}`} />
            <TextField label="Cortesías niños" type="number" size="small"
              value={cortesiasNinos}
              onChange={e => setCortesiasNinos(Math.max(0, Math.min(ninos, parseInt(e.target.value) || 0)))}
              inputProps={{ min: 0, max: ninos }}
              helperText={`Máx ${ninos}`} />
          </Box>
          {(cortesiasAdultos + cortesiasNinos) > 0 && (
            <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
              <TextField label="Autorizada por (nombre)" size="small" fullWidth required
                value={cortesiasAutorizadaPorNombre}
                onChange={e => setCortesiasAutorizadaPorNombre(e.target.value)}
                placeholder="Ej. María del Pilar Salgado"
                helperText="Nombre de la persona que autorizó la cortesía" />
              <TextField label="Motivo" size="small" fullWidth required multiline minRows={2}
                value={cortesiasMotivo}
                onChange={e => setCortesiasMotivo(e.target.value)}
                placeholder="Ej. Familiar del vendedor · Promoción radio · Influencer cliente"
                helperText="Explica brevemente por qué se otorga la cortesía" />
            </Box>
          )}
        </Box>

        {salidaSeleccionada && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f0fdfa', borderRadius: 2 }}>
            <Typography sx={{ fontSize: 11, color: '#475569' }}>
              Adulto {fmtMoney(salidaSeleccionada.precio_adulto)} · Niño {fmtMoney(salidaSeleccionada.precio_nino)} · Bebé {fmtMoney(salidaSeleccionada.precio_bebe)}
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#0d9488', mt: 0.5 }}>
              Total: {fmtMoney(precioTotal)}
            </Typography>
            {(cortesiasAdultos + cortesiasNinos) > 0 && (
              <Typography sx={{ fontSize: 11, color: '#a16207', mt: 0.5 }}>
                Embarcan {adultos + ninos + bebes} personas · Pagan {Math.max(0, adultos - cortesiasAdultos) + Math.max(0, ninos - cortesiasNinos)} · {cortesiasAdultos + cortesiasNinos} cortesía{(cortesiasAdultos + cortesiasNinos) > 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {/* Forma de pago */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', mb: 1 }}>
          <PaymentIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
          Forma de pago
        </Typography>
        <RadioGroup value={formaPago} onChange={e => setFormaPago(e.target.value)}>
          {FORMA_PAGO_OPCIONES.map(o => (
            <Box key={o.value} sx={{
              border: '1px solid', borderColor: formaPago === o.value ? '#0d9488' : '#e2e8f0',
              borderRadius: 2, p: 1, mb: 0.75, bgcolor: formaPago === o.value ? '#f0fdfa' : '#fff',
            }}>
              <FormControlLabel value={o.value} control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{o.label}</Typography>
                    <Typography sx={{ fontSize: 11, color: '#64748b' }}>{o.desc}</Typography>
                  </Box>
                } />
            </Box>
          ))}
        </RadioGroup>
        {formaPago === 'credito' && (
          <TextField sx={{ mt: 1 }} fullWidth size="small" type="number" label="Abono cobrado en agencia"
            placeholder="0"
            value={montoCobrado} onChange={e => setMontoCobrado(e.target.value)}
            helperText="Cuánto cobró la agencia al cliente (informativo). El total se carga al cupo igual."
          />
        )}
        {formaPago === 'transferencia' && (() => {
          const mt = Number(montoTransferido) || precioTotal;
          const diff = Math.max(precioTotal - mt, 0);
          const esParcial = diff > 0.01;
          return (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontSize: 11, color: '#475569', fontWeight: 700, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Soporte de la transferencia
            </Typography>
            <TextField
              fullWidth size="small"
              label="Número de referencia *"
              value={referenciaTransferencia}
              onChange={e => setReferenciaTransferencia(e.target.value)}
              placeholder="Ej: 0123456789"
            />
            {/* Monto transferido */}
            <TextField
              sx={{ mt: 1.5 }} fullWidth size="small" type="number"
              label="Monto transferido"
              value={montoTransferido}
              onChange={e => setMontoTransferido(e.target.value)}
              placeholder={`Total: ${fmtMoney(precioTotal)}`}
              inputProps={{ min: 0, max: precioTotal, step: 1000 }}
              helperText={
                esParcial
                  ? `Faltan ${fmtMoney(diff)} por cubrir`
                  : `Cubre el total de la reserva (${fmtMoney(precioTotal)})`
              }
              error={Number(montoTransferido) > precioTotal + 0.01}
            />
            {/* A cargo de quien queda el saldo */}
            {esParcial && (
              <Box sx={{ mt: 1.5, p: 1, bgcolor: '#fffbeb', borderRadius: 1, border: '1px dashed #fbbf24' }}>
                <Typography sx={{ fontSize: 11, color: '#a16207', fontWeight: 700, mb: 0.75 }}>
                  Saldo de {fmtMoney(diff)} a cargo de:
                </Typography>
                <RadioGroup value={saldoACargoDe} onChange={e => setSaldoACargoDe(e.target.value)}>
                  <FormControlLabel value="agencia" control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Agencia (cupo de crédito)</Typography>
                        <Typography sx={{ fontSize: 10, color: '#78350f' }}>
                          Se carga al cupo de la agencia. Si no tiene cupo suficiente, la reserva no se podrá crear.
                        </Typography>
                      </Box>
                    } />
                  <FormControlLabel value="muelle" control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Turista en muelle</Typography>
                        <Typography sx={{ fontSize: 10, color: '#78350f' }}>
                          El turista paga el saldo al embarcar. La agencia es responsable hasta el cobro.
                        </Typography>
                      </Box>
                    } />
                </RadioGroup>
              </Box>
            )}
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.5 }}>
                Comprobante (foto/PDF) *
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button variant="outlined" size="small" component="label" disabled={subiendoComprobante}>
                  {subiendoComprobante ? 'Subiendo...' : 'Elegir archivo'}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setSubiendoComprobante(true);
                      try {
                        const fd = new FormData();
                        fd.append('archivo', file);
                        const r = await fetch(`${API_BASE}/turismo/soportes/upload`, { method: 'POST', body: fd });
                        if (!r.ok) {
                          const err = await r.json().catch(() => ({}));
                          alert(err.detail || 'Error subiendo comprobante');
                        } else {
                          const data = await r.json();
                          setComprobanteUrl(data.url);
                          setComprobanteNombre(file.name);
                        }
                      } catch (err) {
                        alert('Error subiendo comprobante: ' + err.message);
                      }
                      setSubiendoComprobante(false);
                    }}
                  />
                </Button>
                {comprobanteUrl && (
                  <Typography sx={{ fontSize: 11, color: '#16a34a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    ✓ {comprobanteNombre || 'Archivo subido'}
                  </Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 0.75 }}>
                La reserva quedará en HOLD hasta que Entretur verifique la transferencia.
              </Typography>
            </Box>
          </Box>
          );
        })()}
      </Paper>

      {/* Comisión anticipada */}
      {comisionTotal > 0 && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={comisionAnticipadaCheck}
                onChange={e => {
                  setComisionAnticipadaCheck(e.target.checked);
                  if (!e.target.checked) setComisionAnticipada(0);
                  else setComisionAnticipada(comisionTotal);
                }}
              />
            }
            label={
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                  La agencia cobró comisión por anticipado
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                  Comisión total de esta reserva: <strong>{fmtMoney(comisionTotal)}</strong>
                </Typography>
              </Box>
            }
          />
          {comisionAnticipadaCheck && (
            <Box sx={{ mt: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Monto cobrado por anticipado"
                value={comisionAnticipada}
                onChange={e => setComisionAnticipada(e.target.value)}
                inputProps={{ min: 0, max: comisionTotal, step: 1000 }}
                helperText={`Máximo ${fmtMoney(comisionTotal)} (comisión total). Puede ser menor si cobró parcial.`}
                error={Number(comisionAnticipada) > comisionTotal}
              />
              {Number(comisionAnticipada) > 0 && Number(comisionAnticipada) < comisionTotal && (
                <Alert severity="info" sx={{ mt: 1, fontSize: 11 }}>
                  Quedará pendiente <strong>{fmtMoney(comisionTotal - Number(comisionAnticipada))}</strong> de comisión por cobrar después.
                </Alert>
              )}
            </Box>
          )}

          {/* Descuento sobre la comisión (la agencia regala parte al turista) */}
          <Divider sx={{ my: 1.5 }} />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={descuentoComisionCheck}
                onChange={e => {
                  setDescuentoComisionCheck(e.target.checked);
                  if (!e.target.checked) setDescuentoComision(0);
                }}
              />
            }
            label={
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                  La agencia ofreció un descuento al turista
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                  Sale de la comisión de la agencia (no del precio del tour). Reduce el saldo del turista en muelle.
                </Typography>
              </Box>
            }
          />
          {descuentoComisionCheck && (() => {
            const antEfectivo = comisionAnticipadaCheck ? (Number(comisionAnticipada) || 0) : 0;
            const descMaxPermitido = Math.max(comisionTotal - antEfectivo, 0);
            return (
            <Box sx={{ mt: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Descuento ofrecido"
                value={descuentoComision}
                onChange={e => setDescuentoComision(e.target.value)}
                inputProps={{ min: 0, max: descMaxPermitido, step: 1000 }}
                helperText={`Máximo ${fmtMoney(descMaxPermitido)} (comisión total − anticipo). La agencia solo puede regalar lo que aún le queda de comisión.`}
                error={Number(descuentoComision) > descMaxPermitido + 0.01}
              />
              {Number(descuentoComision) > 0 && (
                <Alert severity="success" sx={{ mt: 1, fontSize: 11 }}>
                  El turista pagará efectivamente <strong>{fmtMoney(precioTotal - Number(descuentoComision))}</strong> en total. La agencia absorbe el descuento de su comisión.
                </Alert>
              )}
            </Box>
            );
          })()}
        </Paper>
      )}

      {/* Cliente */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', mb: 1 }}>
          <PersonIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
          Cliente / titular
        </Typography>
        <TextField fullWidth size="small" label="Nombre completo" value={titularNombre}
          onChange={e => setTitularNombre(e.target.value)} sx={{ mb: 1.5 }} required />
        <TextField fullWidth size="small" label="Cédula / documento" value={cedulaTitular}
          onChange={e => setCedulaTitular(e.target.value)} sx={{ mb: 1.5 }} required />
        <TextField fullWidth size="small" label="WhatsApp del cliente" type="tel" value={whatsappCliente}
          onChange={e => setWhatsappCliente(e.target.value)} placeholder="+57..." required />
      </Paper>

      {/* Opcionales */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', mb: 1 }}>
          <HotelIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
          Opcional
        </Typography>
        <TextField fullWidth size="small" label="Hotel del huésped" value={hotelHuesped}
          onChange={e => setHotelHuesped(e.target.value)} sx={{ mb: 1.5 }} />
        <TextField fullWidth size="small" label="Tiquete / Ref. agencia" value={tiqueteAgencia}
          onChange={e => setTiqueteAgencia(e.target.value)} sx={{ mb: 1.5 }} />
        <TextField fullWidth size="small" multiline rows={2} label="Observaciones"
          value={observaciones} onChange={e => setObservaciones(e.target.value)} />
      </Paper>

      {/* Resumen financiero — el que verá el operador y entiende qué se cobró cuándo */}
      {precioTotal > 0 && (() => {
        const desc = descuentoComisionCheck ? Number(descuentoComision) || 0 : 0;
        const ant = comisionAnticipadaCheck ? Number(comisionAnticipada) || 0 : 0;
        const saldoMuelle = formaPago === 'muelle' ? Math.max(precioTotal - desc - ant, 0) : 0;
        const cobradoCredito = formaPago === 'credito' ? Number(montoCobrado) || 0 : 0;
        return (
          <Paper sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', mb: 1 }}>
              💰 Resumen
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, mb: 0.5 }}>
              <span>Precio del tour (lista)</span>
              <strong>{fmtMoney(precioTotal)}</strong>
            </Box>
            {desc > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, mb: 0.5, color: '#16a34a' }}>
                <span>Descuento (sale de comisión agencia)</span>
                <strong>− {fmtMoney(desc)}</strong>
              </Box>
            )}
            {ant > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, mb: 0.5 }}>
                <span>Cobrado por anticipado (a la agencia)</span>
                <strong>{fmtMoney(ant)}</strong>
              </Box>
            )}
            {cobradoCredito > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, mb: 0.5 }}>
                <span>Cobrado en agencia (crédito)</span>
                <strong>{fmtMoney(cobradoCredito)}</strong>
              </Box>
            )}
            <Divider sx={{ my: 0.75 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, color: formaPago === 'muelle' ? '#dc2626' : '#0f172a' }}>
              <span>{formaPago === 'muelle' ? 'Saldo turista paga en muelle' : 'Saldo turista'}</span>
              <span>{fmtMoney(saldoMuelle)}</span>
            </Box>
            {comisionTotal > 0 && (
              <Typography sx={{ fontSize: 10, color: '#64748b', mt: 1 }}>
                Comisión efectiva agencia: {fmtMoney(Math.max(comisionTotal - desc, 0))}
                {desc > 0 && ` (${fmtMoney(comisionTotal)} − ${fmtMoney(desc)} descuento)`}
              </Typography>
            )}
          </Paper>
        );
      })()}

      {/* Consentimiento DIMAR (Ley 1581/2012) */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: consentimientoDimar ? '#f0fdf4' : '#fef3c7',
                    border: '1px solid', borderColor: consentimientoDimar ? '#86efac' : '#fcd34d' }}>
        <FormControlLabel
          control={
            <Checkbox size="small" checked={consentimientoDimar}
              onChange={e => setConsentimientoDimar(e.target.checked)} />
          }
          label={
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                Consentimiento DIMAR *
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#475569', lineHeight: 1.4 }}>
                Acepto que al embarcar se capture una foto del titular sosteniendo su documento
                de identidad (cédula o pasaporte), conforme a las normas de operación marítima
                colombiana (DIMAR) y la Ley 1581/2012 de protección de datos. Los datos se
                conservan 12 meses y luego se anonimizan.
              </Typography>
            </Box>
          }
          sx={{ alignItems: 'flex-start', m: 0 }}
        />
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Button fullWidth variant="contained" size="large" disabled={submitting || !salidaId || !consentimientoDimar}
        onClick={submit}
        sx={{
          py: 1.5, fontSize: 16, fontWeight: 700, textTransform: 'none',
          background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
          boxShadow: '0 4px 16px rgba(13, 148, 136, 0.4)',
          '&:hover': { background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)' },
        }}>
        {submitting ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Confirmar reserva'}
      </Button>

      <Typography sx={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', mt: 2 }}>
        Al confirmar, recibirás el código de reserva por WhatsApp.
      </Typography>
      <Typography sx={{ fontSize: 8, color: '#cbd5e1', textAlign: 'center', mt: 0.5 }}>
        {FORM_VERSION}
      </Typography>
    </Box>
  );
}


/**
 * PantallaPagoBold — Pantalla post-success cuando la reserva se creó con
 * forma_pago='bold_link'. Hace polling al estado del pago y cambia la UI:
 *   - pendiente: muestra link + 3 botones (Pagar / Copiar / WhatsApp) + spinner discreto
 *   - pagado:    "Pago exitoso" verde, oculta link y botones (ya no sirve)
 *   - rechazado: "Pago rechazado" rojo, botón "Generar nuevo link"
 *   - expirado:  "Link expirado" gris, botón "Generar nuevo link"
 *
 * Polling con backoff:
 *   - Primeros 60s: cada 3s (pago suele confirmarse rápido)
 *   - Siguientes 4min: cada 10s
 *   - Después de 5min: detiene polling, muestra mensaje "refresca la página"
 */
function PantallaPagoBold({ success, titularNombre, whatsappCliente, compartirBoldWa, onReintentar }) {
  const [estado, setEstado] = useState('pendiente');
  const [info, setInfo] = useState(null);
  const [polling, setPolling] = useState(true);

  useEffect(() => {
    if (!success.payment_link_id || !polling) return;
    let cancel = false;
    let timer = null;
    const inicio = Date.now();
    const apiBase = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

    const consultar = async () => {
      if (cancel) return;
      try {
        const r = await fetch(`${apiBase}/turismo/reserva-form/estado-pago/${success.payment_link_id}`);
        if (r.ok) {
          const d = await r.json();
          if (cancel) return;
          setInfo(d);
          if (d.estado && d.estado !== estado) setEstado(d.estado);
          // Estados terminales: detener polling
          if (d.estado === 'pagado' || d.estado === 'rechazado' || d.estado === 'expirado') {
            setPolling(false);
            return;
          }
        }
      } catch {}
      // Cadencia con backoff
      const transcurrido = Date.now() - inicio;
      if (transcurrido > 5 * 60 * 1000) {
        setPolling(false);
        return;
      }
      const proximo = transcurrido < 60 * 1000 ? 3000 : 10000;
      timer = setTimeout(consultar, proximo);
    };
    consultar();
    return () => { cancel = true; if (timer) clearTimeout(timer); };
  }, [success.payment_link_id, polling, estado]);

  return (
    <Box sx={{ p: 3, maxWidth: 480, mx: 'auto', mt: 6 }}>
      <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>

        {/* ── Estado: PAGADO ── */}
        {estado === 'pagado' && (
          <>
            <CheckCircleIcon sx={{ fontSize: 72, color: '#10b981', mb: 1.5 }} />
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
              ¡Pago exitoso!
            </Typography>
            <Typography sx={{ fontSize: 14, color: '#64748b', mb: 2 }}>
              Tu reserva quedó confirmada.
            </Typography>
            <Chip label={success.codigo_reserva}
              sx={{ fontSize: 18, fontWeight: 700, py: 2.5, px: 1, mb: 2, bgcolor: '#0d9488', color: '#fff' }} />
            <Box sx={{ p: 2, bgcolor: '#f0fdf4', border: '1px solid #86efac', borderRadius: 2, mt: 2, textAlign: 'left' }}>
              <Typography sx={{ fontSize: 11, color: '#15803d', fontWeight: 700, textTransform: 'uppercase', mb: 1 }}>
                Detalles del pago
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#0f172a' }}>
                <strong>Monto:</strong> {fmtMoney(info?.monto || success.precio_total)}<br/>
                {info?.payment_method && <><strong>Método:</strong> {info.payment_method}<br/></>}
                {info?.transaction_id && <><strong>Transacción:</strong> {info.transaction_id}<br/></>}
                <strong>Titular:</strong> {titularNombre}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 2 }}>
              También recibirás esta confirmación por WhatsApp.
            </Typography>
          </>
        )}

        {/* ── Estado: RECHAZADO ── */}
        {estado === 'rechazado' && (
          <>
            <Box sx={{ fontSize: 72, color: '#dc2626', mb: 1.5 }}>⛔</Box>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
              Pago rechazado
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#64748b', mb: 2 }}>
              El pago fue rechazado. Genera un nuevo link e intenta de nuevo.
            </Typography>
            <Chip label={success.codigo_reserva}
              sx={{ fontSize: 14, fontWeight: 600, mb: 2, bgcolor: '#fee2e2', color: '#991b1b' }} />
            <Button fullWidth variant="contained" onClick={onReintentar}
              sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#0d9488' }}>
              Volver al formulario
            </Button>
          </>
        )}

        {/* ── Estado: EXPIRADO ── */}
        {estado === 'expirado' && (
          <>
            <Box sx={{ fontSize: 72, color: '#94a3b8', mb: 1.5 }}>⌛</Box>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
              Link expirado
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#64748b', mb: 2 }}>
              Este link ya no es válido. Genera uno nuevo.
            </Typography>
            <Chip label={success.codigo_reserva}
              sx={{ fontSize: 14, fontWeight: 600, mb: 2, bgcolor: '#f1f5f9', color: '#475569' }} />
            <Button fullWidth variant="contained" onClick={onReintentar}
              sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#0d9488' }}>
              Volver al formulario
            </Button>
          </>
        )}

        {/* ── Estado: PENDIENTE (default mientras esperamos) ── */}
        {estado === 'pendiente' && (
          <>
            <CheckCircleIcon sx={{ fontSize: 72, color: '#10b981', mb: 1.5 }} />
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
              ¡Reserva creada!
            </Typography>
            <Typography sx={{ fontSize: 14, color: '#64748b', mb: 2 }}>Código:</Typography>
            <Chip label={success.codigo_reserva}
              sx={{ fontSize: 18, fontWeight: 700, py: 2.5, px: 1, mb: 2, bgcolor: '#0d9488', color: '#fff' }} />

            <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: '#f5f3ff', border: '1px solid #c4b5fd' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#5b21b6', mb: 1 }}>
                💳 Link de pago BOLD generado
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#475569', mb: 1.5, textAlign: 'left' }}>
                La reserva queda en HOLD. Usa <strong>Pagar ahora</strong> si tú haces el pago al instante,
                o <strong>Enviar por WhatsApp</strong> para que el turista lo pague desde su celular.
              </Typography>
              <Box sx={{ p: 1, bgcolor: '#fff', borderRadius: 1, mb: 1, border: '1px solid #ddd6fe' }}>
                <Typography sx={{ fontSize: 11, fontFamily: 'monospace', wordBreak: 'break-all', color: '#0f172a' }}>
                  {success.payment_url}
                </Typography>
              </Box>
              <Button fullWidth size="medium" variant="contained"
                onClick={() => window.open(success.payment_url, '_blank', 'noopener,noreferrer')}
                sx={{ textTransform: 'none', fontWeight: 700, mb: 1,
                       background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' }}>
                💳 Pagar ahora
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button fullWidth size="small" variant="outlined"
                  onClick={() => navigator.clipboard?.writeText(success.payment_url)}
                  sx={{ textTransform: 'none', borderColor: '#7c3aed', color: '#7c3aed' }}>
                  Copiar URL
                </Button>
                <Button fullWidth size="small" variant="contained" onClick={compartirBoldWa}
                  sx={{ textTransform: 'none', fontWeight: 700,
                         background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}>
                  Enviar por WhatsApp
                </Button>
              </Box>
            </Box>

            {/* Indicador de polling */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2 }}>
              {polling ? (
                <>
                  <CircularProgress size={14} sx={{ color: '#0d9488' }} />
                  <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                    Esperando confirmación de pago…
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                  Sigue pendiente. Refresca la página para verificar.
                </Typography>
              )}
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}
